/**
 * Hook for managing POS print configuration
 */

import { useState, useCallback, useEffect } from "react";
import { useToast } from "./useToast";
import {
  PosPrintConfig,
  defaultPosPrintConfig,
  PosPrintFieldKey,
} from "../config/posPrintConfig";

export const usePosPrintConfig = (storageKey = "pos_print_config") => {
  const [config, setConfig] = useState<PosPrintConfig>(defaultPosPrintConfig);
  const [isLoading, setIsLoading] = useState(true);

  const toast = useToast();
  // Load config from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConfig(normalizePosPrintConfig(parsed));
      }
    } catch (error) {
      toast.showError("Failed to load POS print config from storage.");
    } finally {
      setIsLoading(false);
    }
  }, [storageKey, toast]);

  const saveConfig = useCallback(
    (newConfig: PosPrintConfig) => {
      try {
        setConfig(newConfig);
        localStorage.setItem(storageKey, JSON.stringify(newConfig));
      } catch (error) {
        toast.showError("Failed to save POS print config to storage.");
      }
    },
    [storageKey, toast]
  );

  const updateFieldPosition = useCallback(
    (fieldKey: PosPrintFieldKey, top: string, left: string, visible?: boolean) => {
      setConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          [fieldKey]: {
            ...prev.fields[fieldKey],
            top,
            left,
            ...(visible !== undefined && { visible }),
          },
        },
      }));
    },
    []
  );

  const resetToDefaults = useCallback(() => {
    setConfig(defaultPosPrintConfig);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  return {
    config,
    isLoading,
    saveConfig,
    updateFieldPosition,
    resetToDefaults,
  };
};

/**
 * Normalizes raw configuration data to ensure all required fields are present
 */
export const normalizePosPrintConfig = (raw: any): PosPrintConfig => {
  const defaults = defaultPosPrintConfig;
  const source = raw || {};

  const stripUnit = (value: unknown): string => {
    if (value === undefined || value === null) return "";
    return String(value).replace(/cm$/, "");
  };

  const normalizeVisibility = (value: unknown): boolean => {
    if (value === undefined || value === null) return true;
    return !(value === false || value === 0 || value === "0");
  };

  const normalizeField = (fieldKey: PosPrintFieldKey) => {
    const field = source?.fields?.[fieldKey];

    if (field?.top !== undefined && field?.left !== undefined) {
      return {
        top: stripUnit(field.top),
        left: stripUnit(field.left),
        visible: normalizeVisibility(field.visible),
      };
    }

    return {
      top: stripUnit(defaults.fields[fieldKey].top),
      left: stripUnit(defaults.fields[fieldKey].left),
      visible: defaults.fields[fieldKey].visible,
    };
  };

  const fieldKeys: PosPrintFieldKey[] = [
    "store_name",
    "store_address",
    "invoice_number",
    "date_time",
    "cashier_name",
    "customer_name",
    "transaction_type",
    "payment_method",
    "subtotal",
    "discount_label",
    "discount_amount",
    "total_amount",
  ];

  const normalizedFields: Record<PosPrintFieldKey, any> = {} as any;
  fieldKeys.forEach((key) => {
    normalizedFields[key] = normalizeField(key);
  });

  return {
    page: {
      width: stripUnit(source?.page?.width ?? defaults.page.width),
      height: stripUnit(source?.page?.height ?? defaults.page.height),
    },
    layout: {
      left_margin: stripUnit(source?.layout?.left_margin ?? defaults.layout.left_margin),
      right_margin: stripUnit(source?.layout?.right_margin ?? defaults.layout.right_margin),
    },
    sections: {
      header: {
        top: stripUnit(source?.sections?.header?.top ?? defaults.sections.header.top),
        left: stripUnit(source?.sections?.header?.left ?? defaults.sections.header.left),
        height: stripUnit(source?.sections?.header?.height ?? defaults.sections.header.height),
      },
      table: {
        top: stripUnit(source?.sections?.table?.top ?? defaults.sections.table.top),
        left: stripUnit(source?.sections?.table?.left ?? defaults.sections.table.left),
        width: stripUnit(source?.sections?.table?.width ?? defaults.sections.table.width),
        row_height: stripUnit(source?.sections?.table?.row_height ?? defaults.sections.table.row_height),
      },
      footer: {
        top: stripUnit(source?.sections?.footer?.top ?? defaults.sections.footer.top),
        height: stripUnit(source?.sections?.footer?.height ?? defaults.sections.footer.height),
      },
    },
    fields: normalizedFields,
    tableMeta: {
      bottom_padding: source?.tableMeta?.bottom_padding ?? defaults.tableMeta?.bottom_padding,
      footer_height: source?.tableMeta?.footer_height ?? defaults.tableMeta?.footer_height,
    },
  };
};
